import { useState, useEffect, useContext } from "react";
import { Fragment } from "react";
import { ModalContext } from "app/app";
import { X, Edit, Trash2, Plus } from "feather-icons-react";
import styles from "./styles.module.scss";
import { messageTemplateService } from "../../../services/messageTemplateService";
// Tool type labels for display
const TOOL_TYPE_LABELS = {
  service_center: "Service Center",
  renewals: "Renewals", 
  bind_requests: "Bind Requests",
  endorsements: "Endorsements"
};

export function MessageTemplatesModal() {
  const { modal, setModal } = useContext(ModalContext);
  
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ title: "", message: "", category: "" });

  // Get tool type from modal data
  const toolType = modal.data?.toolType || "service_center";
  const onTemplateSelect = modal.data?.onTemplateSelect;
  const keepParentOpen = modal.data?.keepParentOpen || false;
  const parentModal = modal.data?.parentModal;
  const parentData = modal.data?.parentData;

  // Load templates from API
  useEffect(() => {
    if (modal.name === "message_templates_modal" && toolType) {
      loadTemplates();
    }
  }, [modal.name, toolType]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const apiTemplates = await messageTemplateService.getTemplates(toolType);
      setTemplates(apiTemplates);
    } catch (error) {
      console.error("Error loading templates:", error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
    
    if (keepParentOpen && parentModal) {
      // Return to the parent modal with the template message
      setModal({
        name: parentModal,
        data: {
          ...parentData,
          templateMessage: template.message
        }
      });
    } else {
      setModal("");
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      message: template.message,
      category: template.category
    });
  };

  const handleDeleteTemplate = (template) => {
    setDeletingTemplate(template);
  };

  const handleCreateTemplate = () => {
    setShowCreateForm(true);
    setFormData({ title: "", message: "", category: "" });
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      
      if (editingTemplate) {
        // Update existing template
        await messageTemplateService.updateTemplate(editingTemplate.id, {
          title: formData.title,
          message: formData.message,
          category: formData.category,
        });
      } else {
        // Create new template
        await messageTemplateService.createTemplate({
          title: formData.title,
          message: formData.message,
          category: formData.category,
          tool_type: toolType,
        });
      }
      
      // Reload templates
      await loadTemplates();
      
      // Reset form
      setEditingTemplate(null);
      setShowCreateForm(false);
      setFormData({ title: "", message: "", category: "" });
    } catch (error) {
      console.error("Error saving template:", error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await messageTemplateService.deleteTemplate(deletingTemplate.id);
      
      // Reload templates
      await loadTemplates();
      
      setDeletingTemplate(null);
    } catch (error) {
      console.error("Error deleting template:", error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    if (keepParentOpen && parentModal) {
      // Return to the parent modal instead of closing everything
      setModal({
        name: parentModal,
        data: parentData
      });
    } else {
      setModal("");
    }
    setEditingTemplate(null);
    setDeletingTemplate(null);
    setShowCreateForm(false);
    setFormData({ title: "", message: "", category: "" });
  };

  const handleCancelForm = () => {
    setEditingTemplate(null);
    setShowCreateForm(false);
    setFormData({ title: "", message: "", category: "" });
  };

  const handleCancelDelete = () => {
    setDeletingTemplate(null);
  };

  if (modal.name !== "message_templates_modal") {
    return null;
  }

  return (
    <Fragment>
      <div className={styles.background}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <h2>Message Templates</h2>
            <p>Select a template for {TOOL_TYPE_LABELS[toolType] || toolType}</p>
            <button className={styles.closeButton} onClick={handleModalClose}>
              <X size={20} />
            </button>
          </div>

          <div className={styles.content}>
            <div className={styles.templateList}>
              <div className={styles.listHeader}>
                <h3>Available Templates</h3>
              </div>

              {loading ? (
                <div className={styles.loading}>Loading templates...</div>
              ) : templates.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No templates available for {TOOL_TYPE_LABELS[toolType] || toolType}</p>
                </div>
              ) : (
                <div className={styles.templates}>
                  {templates.map((template) => (
                    <div key={template.id} className={styles.template}>
                      <div className={styles.templateContent} onClick={() => handleTemplateSelect(template)}>
                        <div className={styles.templateHeader}>
                          <h4>{template.title}</h4>
                          {template.category && (
                            <span className={styles.templateCategory}>{template.category}</span>
                          )}
                        </div>
                        <p>{template.message.substring(0, 120)}...</p>
                      </div>
                      <div className={styles.templateActions}>
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTemplate(template);
                          }}
                          title="Edit template"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template);
                          }}
                          title="Delete template"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className={styles.addTemplateSection}>
                <button
                  className={styles.addButton}
                  onClick={handleCreateTemplate}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Template Modal */}
      {(showCreateForm || editingTemplate) && (
        <div className={styles.background}>
          <div className={styles.formWrapper}>
            <div className={styles.header}>
              <h2>{editingTemplate ? "Edit Template" : "New Template"}</h2>
              <button className={styles.closeButton} onClick={handleCancelForm}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.formContent}>
              <div className={styles.formGroup}>
                <label>Title</label>
                <input
                  type="text"
                  placeholder="Enter..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Message</label>
                <textarea
                  placeholder="Enter text here..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={8}
                />
              </div>

              <div className={styles.formActions}>
                <button
                  className={styles.cancelButton}
                  onClick={handleCancelForm}
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmButton}
                  onClick={handleSaveTemplate}
                  disabled={!formData.title.trim() || !formData.message.trim()}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTemplate && (
        <div className={styles.background}>
          <div className={styles.deleteWrapper}>
            <div className={styles.deleteContent}>
              <h2>Delete Template</h2>
              <p>Are you sure you want to delete this template?</p>
              
              <div className={styles.deleteActions}>
                <button
                  className={styles.cancelButton}
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmButton}
                  onClick={handleConfirmDelete}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
} 